import { useState } from "react";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  Container,
  Group,
  LoadingOverlay,
  Notification,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";

const LicenseVerify = () => {
  const [githubUsername, setGithubUsername] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!githubUsername.trim() || !licenseKey.trim()) {
      setNotification({
        type: "error",
        message: "Please fill in both GitHub username and license key",
      });
      return;
    }

    setLoading(true);
    setNotification(null);

    try {
      const response = await fetch(
        "https://api.scaletozeroaws.com/v1/user/license/verify",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            githubUsername: githubUsername.trim(),
            licenseKey: licenseKey.trim(),
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setNotification({
          type: "success",
          message:
            data.message ||
            "License verified successfully! You have been invited to the private repository.",
        });
        // Clear form on success
        setGithubUsername("");
        setLicenseKey("");
      } else {
        setNotification({
          type: "error",
          message:
            data.message ||
            "Failed to verify license. Please check your details and try again.",
        });
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Card shadow="sm" padding="xl" radius="md" withBorder>
        <LoadingOverlay visible={loading} overlayBlur={2} />

        <Stack spacing="lg">
          <div>
            <Title order={1} align="center" mb="xs">
              Activate Your AppSumo Purchase
            </Title>
            <Text size="sm" color="dimmed" align="center">
              Enter your GitHub username and AppSumo license key to get access
              to the private repository
            </Text>
          </div>

          {notification && (
            <Notification
              icon={
                <FontAwesomeIcon
                  icon={notification.type === "success" ? faCheck : faTimes}
                />
              }
              color={notification.type === "success" ? "green" : "red"}
              onClose={() => setNotification(null)}
            >
              {notification.message}
            </Notification>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing="md">
              <TextInput
                label="GitHub Username"
                placeholder="Enter your GitHub username"
                value={githubUsername}
                onChange={(e) => setGithubUsername(e.target.value)}
                required
                description="Your GitHub account will be invited to the private repository"
              />

              <TextInput
                label="AppSumo License Key"
                placeholder="Enter your AppSumo license key"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                required
                description="The license key you received from AppSumo"
              />

              <Group position="center" mt="md">
                <Button type="submit" size="md" disabled={loading}>
                  {loading ? "Activating..." : "Activate License"}
                </Button>
              </Group>
            </Stack>
          </form>

          <div>
            <Text size="xs" color="dimmed" align="center">
              Having trouble? Contact us at{" "}
              <a href="mailto:support@scaletozeroaws.com">
                support@scaletozeroaws.com
              </a>
            </Text>
          </div>
        </Stack>
      </Card>
    </Container>
  );
};

export default LicenseVerify;
